import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaOnboardingTasksV2Service, serviceName } from '../service';
import { GetLocationTaskStatsForMultiIO, ServiceQueries } from '../types';
import { useOnboardingTasksV2Queries } from './use-onboarding-tasks-v2-queries';
import { UseOnboardingTasksV2QueryEndpointArgs, useOnboardingTasksV2Query } from './use-onboarding-tasks-v2-query';

/**
 * A hook that returns a query for the `GetLocationTaskStatsForMulti` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLocationTaskStatsForMultiQuery = <E = unknown, D = GetLocationTaskStatsForMultiIO['output']>(
  args: UseOnboardingTasksV2QueryEndpointArgs<'GetLocationTaskStatsForMulti', E, D>
) =>
  useOnboardingTasksV2Query({
    endpointName: 'GetLocationTaskStatsForMulti',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetLocationTaskStatsForMulti` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetLocationTaskStatsForMultiQueries = <E = unknown, D = GetLocationTaskStatsForMultiIO['output']>(
  argsArr: UseOnboardingTasksV2QueryEndpointArgs<'GetLocationTaskStatsForMulti', E, D>[]
) =>
  useOnboardingTasksV2Queries(
    argsArr.map((args) => ({
      endpointName: 'GetLocationTaskStatsForMulti',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetLocationTaskStatsForMulti` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetLocationTaskStatsForMulti = () => {
  const queryClient = useQueryClient();

  const fetchGetLocationTaskStatsForMulti = async <E = unknown, D = GetLocationTaskStatsForMultiIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseOnboardingTasksV2QueryEndpointArgs<'GetLocationTaskStatsForMulti', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetLocationTaskStatsForMultiIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLocationTaskStatsForMulti'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetLocationTaskStatsForMultiIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetLocationTaskStatsForMulti'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetLocationTaskStatsForMulti'>({
          serviceName,
          endpointName: 'GetLocationTaskStatsForMulti',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaOnboardingTasksV2Service.GetLocationTaskStatsForMulti(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetLocationTaskStatsForMulti;
};
